/// Letter grade derived from how cleanly the player flew a level.
///
/// "Infringement" = one unique pair-of-aircraft entering a separation
/// conflict (within 2 NM AND 500 ft, but not yet a crash). Crashes
/// are separate — they already cost the aircraft and −500 points,
/// so we don't double-count them in this rate.
///
/// Scaled by handled aircraft so a level with 12 arrivals isn't
/// punished against a 4-arrival tutorial. A run with zero
/// infringements is A+ regardless of count — perfect separation is
/// the gold standard. After that the bands track typical play:
///
///   < 0.10 / handled  → A      (a stray near-miss in a busy session)
///   < 0.25            → B      (under control)
///   < 0.50            → C      (frequent buzz-bys)
///   < 1.00            → D      (most aircraft saw a conflict)
///   ≥ 1.00            → F      (every aircraft saw at least one)
///
/// `handled === 0` returns A+ — the level didn't produce any traffic
/// to skill-rate, so don't punish for an empty session.

export type SkillRating = 'A+' | 'A' | 'B' | 'C' | 'D' | 'F'

export function skillRating(
  handled: number,
  infringements: number,
): SkillRating {
  if (infringements === 0) return 'A+'
  if (handled === 0) return 'A+'
  const rate = infringements / handled
  if (rate < 0.1) return 'A'
  if (rate < 0.25) return 'B'
  if (rate < 0.5) return 'C'
  if (rate < 1.0) return 'D'
  return 'F'
}

/// Short flavour text for the EndScreen line. Kept brief; the
/// grade itself is the headline.
export function skillRatingDescription(rating: SkillRating): string {
  switch (rating) {
    case 'A+':
      return 'No conflicts'
    case 'A':
      return 'Clean flying'
    case 'B':
      return 'Solid'
    case 'C':
      return 'Some near-misses'
    case 'D':
      return 'Heavy traffic conflicts'
    case 'F':
      return 'Lost the picture'
  }
}
